import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  loginSuccess,
  setTempAccessToken,
  updateTempAccessStatus,
  setTempAccessMonitoring,
  clearTempAccess,
  signOut,
} from '../../store/slices/authSlice';
import { db } from '../../firebase-config';
import { doc, onSnapshot } from 'firebase/firestore';
import tempAccessService from '../../services/tempAccessService';
import sessionManager from '../../services/sessionManager';
import { getDoc as getRoleDoc } from 'firebase/firestore';
// Google auth imports removed - no longer needed
import { doc as firestoreDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

const initialState = {
  step: 'idle', // idle, validating, prompting_otp, verifying_otp, success, error
  token: null,
  linkData: null,
  error: null,
  otpError: null,
  timeLeft: null,
};

function tempAccessReducer(state, action) {
  switch (action.type) {
    case 'VALIDATE_START':
      return { ...initialState, step: 'validating', token: action.payload.token };
    case 'VALIDATE_SUCCESS':
      return { ...state, step: 'prompting_otp', linkData: action.payload.linkData, error: null };
    case 'VALIDATE_ERROR':
      return { ...state, step: 'error', error: action.payload.error };
    case 'VERIFY_OTP_START':
      return { ...state, step: 'verifying_otp', otpError: null };
    case 'VERIFY_OTP_SUCCESS':
      return { ...state, step: 'success' };
    case 'VERIFY_OTP_ERROR':
      return { ...state, otpError: action.payload.error, step: 'prompting_otp' };
    case 'SET_TIME_LEFT':
      return { ...state, timeLeft: action.payload.timeLeft };
    case 'SESSION_EXPIRED':
      return { ...state, step: 'error', error: 'Your temporary access has been revoked or has expired.' };
    case 'CLEAR':
      return initialState;
    default:
      return state;
  }
}

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

const TempAccessHandler = ({ children }) => {
  const [state, localDispatch] = React.useReducer(tempAccessReducer, initialState);
  const dispatch = useDispatch();
  const { tempAccess } = useSelector(state => state.auth);

  // Check URL for token only on initial mount.
  // Also handles auto-timeout: if a stored temp session has autoTimeout enabled
  // and the user has been inactive for > 30 min, force OTP re-verification.
  useEffect(() => {
    let urlToken = null;
    const urlParams = new URLSearchParams(window.location.search);
    urlToken = urlParams.get('temp_access');

    if (!urlToken) {
      const pathParts = window.location.pathname.split('/');
      if (pathParts.length >= 3 && pathParts[1] === 'temp-access') {
        urlToken = pathParts[2];
      }
    }

    if (urlToken) {
      // New token in URL → always start fresh validation + OTP
      localDispatch({ type: 'VALIDATE_START', payload: { token: urlToken } });
      return;
    }

    // No token in URL — check for a stored temp access session that may need re-verification
    const stored = sessionManager.getStoredSession();
    if (stored?.userData?.autoTimeout && stored?.userData?.tempAccessToken) {
      const lastActivity = stored.lastActivity
        ? new Date(stored.lastActivity).getTime()
        : 0;
      const inactiveDuration = Date.now() - lastActivity;

      if (inactiveDuration > INACTIVITY_TIMEOUT_MS) {
        // User was inactive > 30 min AND reloaded → clear session, force OTP
        console.log('⏰ Auto-timeout triggered: inactive for', Math.round(inactiveDuration / 60000), 'min. Requiring OTP re-verification.');
        sessionManager.clearSession();
        dispatch(clearTempAccess());
        localDispatch({ type: 'VALIDATE_START', payload: { token: stored.userData.tempAccessToken } });
        return;
      }
      // User is still active (< 30 min) — let the normal session restore happen
    }

    localDispatch({ type: 'CLEAR' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect to handle validation
  useEffect(() => {
    if (state.step === 'validating') {
      const validate = async () => {
        try {
          const validation = await tempAccessService.validateTempAccess(state.token);
          if (!validation.valid) {
            localDispatch({ type: 'VALIDATE_ERROR', payload: { error: validation.error || 'This temporary access link is invalid or has expired.' } });
            dispatch(clearTempAccess());
            return;
          }

          try {
            await tempAccessService.generateAdminOTP(state.token, validation.email);
          } catch (e) {
            console.warn('⚠️ Failed to generate admin OTP:', e);
          }

          localDispatch({
            type: 'VALIDATE_SUCCESS',
            payload: {
              linkData: {
                tempLinkEmail: validation.email,
                expiresAt: validation.expiresAt,
                role: validation.role,
              },
            },
          });
          dispatch(updateTempAccessStatus({ isValid: true, lastValidated: Date.now() }));
        } catch (error) {
          console.error('❌ Error validating temporary access token:', error);
          localDispatch({ type: 'VALIDATE_ERROR', payload: { error: 'Unable to validate the temporary access link. Please check your connection and try again.' } });
          dispatch(clearTempAccess());
        }
      };
      validate();
    }
  }, [state.step, state.token, dispatch]);

  // The old timer has been removed. The new real-time monitoring effect
  // now handles session expiration more reliably.

  // Real-time monitoring for temp access token revocation and expiration
  useEffect(() => {
    let sessionTimeout = null;

    if (state.token && state.step === 'success') {
      const unsubscribe = onSnapshot(
        doc(db, 'temporary_access', state.token),
        (docSnapshot) => {
          // Clear any existing timeout because we're about to re-evaluate.
          if (sessionTimeout) clearTimeout(sessionTimeout);

          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            let expiresAt = null;
            if (data.expiresAt) {
              // Handle both Firestore Timestamp and ISO string formats
              expiresAt = typeof data.expiresAt.toDate === 'function' 
                ? data.expiresAt.toDate() 
                : new Date(data.expiresAt);
            }

            const isRevoked = !data.isActive || data.revokedAt;
            const isExpired = expiresAt && new Date() >= expiresAt;

            if (isRevoked || isExpired) {
              localDispatch({ type: 'SESSION_EXPIRED' });
              // No need to clean up here, the main return function will do it.
              return;
            }

            // If not revoked or expired, set a client-side timeout as a fallback.
            // This handles the case where the expiration time passes without any DB changes.
            if (expiresAt) {
              const timeUntilExpiry = expiresAt.getTime() - new Date().getTime();
              sessionTimeout = setTimeout(() => {
                localDispatch({ type: 'SESSION_EXPIRED' });
              }, timeUntilExpiry > 0 ? timeUntilExpiry : 0);
            }
          } else {
            // Document was deleted, which is equivalent to being revoked.
            localDispatch({ type: 'SESSION_EXPIRED' });
          }
        },
        (error) => {
          console.error('❌ Error monitoring temp access token:', error);
          localDispatch({ type: 'SESSION_EXPIRED' }); // Log out on error for security
        }
      );

      return () => {
        unsubscribe();
        if (sessionTimeout) clearTimeout(sessionTimeout);
      };
    }
  }, [state.token, state.step]);

  // This effect handles the cleanup once the session has been marked as expired.
  useEffect(() => {
    if (state.step === 'SESSION_EXPIRED') {
      dispatch(clearTempAccess());
      sessionManager.clearSession(); // Use the service to clear session data
    }
  }, [state.step, dispatch]);


  const handleOTPSubmit = async (e, otp) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      localDispatch({ type: 'VERIFY_OTP_ERROR', payload: { error: 'Please enter a valid 6-digit OTP' } });
      return;
    }

    localDispatch({ type: 'VERIFY_OTP_START' });

    try {
      const verify = await tempAccessService.verifyAdminOTP(state.token, otp);
      if (!verify.valid) {
        localDispatch({ type: 'VERIFY_OTP_ERROR', payload: { error: verify.error || 'Invalid OTP. Please check with the admin.' } });
        return;
      }

      const organization = { id: 'drishti_lifesaving_org_id', name: 'Drishti Lifesaving' };
      const role = state.linkData?.role || 'guest';

      let permissions = {};
      try {
        const roleDoc = await getRoleDoc(doc(db, 'roles', role));
        if (roleDoc.exists()) {
          permissions = roleDoc.data().permissions || {};
        }
      } catch { }

      // Read autoTimeout from the temp access link document
      let autoTimeout = false;
      try {
        const linkDoc = await getDoc(doc(db, 'temporary_access', state.token));
        if (linkDoc.exists()) {
          autoTimeout = !!linkDoc.data().autoTimeout;
        }
      } catch (e) { console.warn('Could not read autoTimeout flag:', e); }

      const userData = {
        uid: `temp_${state.token}`,
        email: verify.tempLinkEmail,
        displayName: verify.tempLinkEmail?.split('@')[0] || 'Temp User',
        role,
        permissions,
        organization,
        expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
        autoTimeout,
        tempAccessToken: state.token,
      };

      const sessionResult = await sessionManager.createSession(verify.tempLinkEmail, userData);

      if (sessionResult.success) {
        sessionManager.initActivityTracking();
        dispatch(loginSuccess({
          ...userData,
          sessionId: sessionResult.sessionId,
          sessionExpiresAt: sessionResult.expiresAt,
          lastActivity: new Date().toISOString()
        }));
        dispatch(setTempAccessToken({ token: state.token, isValid: true, expiresAt: userData.expiresAt, email: userData.email }));
        dispatch(setTempAccessMonitoring(true));
        localDispatch({ type: 'VERIFY_OTP_SUCCESS' });
      } else {
        localDispatch({ type: 'VERIFY_OTP_ERROR', payload: { error: 'Failed to create session. Please try again.' } });
      }
    } catch (error) {
      console.error('❌ Error completing temp access login:', error);
      localDispatch({ type: 'VERIFY_OTP_ERROR', payload: { error: 'Login failed. Please try again.' } });
    }
  };

  const handleGenerateNewOTP = async () => {
    localDispatch({ type: 'VERIFY_OTP_START' });
    try {
      await tempAccessService.generateAdminOTP(state.token, state.linkData?.tempLinkEmail);
      localDispatch({ type: 'VALIDATE_SUCCESS', payload: { linkData: state.linkData } });
    } catch (error) {
      console.error('❌ Error generating new OTP:', error);
      localDispatch({ type: 'VERIFY_OTP_ERROR', payload: { error: 'Network error. Please try again.' } });
    }
  };

  const handleReturnToLogin = () => {
    dispatch(signOut());
    window.location.href = '/';
  };

  // Loading state
  if (state.step === 'validating' || state.step === 'verifying_otp') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <h3 style={{ color: '#2c3e50', margin: '0 0 10px 0' }}>
            {state.step === 'validating' ? 'Validating Access Link...' : 'Verifying OTP...'}
          </h3>
          <p style={{ color: '#7f8c8d', margin: '0' }}>Please wait a moment</p>
        </div>
      </div>
    );
  }

  // Error state
  if (state.step === 'error') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxWidth: '500px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
          <h2 style={{ color: '#e74c3c', margin: '0 0 15px 0' }}>Access Denied</h2>
          <p style={{ color: '#2c3e50', margin: '0 0 25px 0', lineHeight: '1.5' }}>{state.error}</p>
          <button
            onClick={handleReturnToLogin}
            style={{
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '16px',
              cursor: 'pointer',
              transition: 'background-color 0.3s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#3498db'}
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }


  // OTP Input step
  if (state.step === 'prompting_otp') {
    return (
      <OTPInputScreen
        linkData={state.linkData}
        otpError={state.otpError}
        isLoading={state.step === 'verifying_otp'}
        onOTPSubmit={handleOTPSubmit}
      />
    );
  }

  // Success state - show countdown timer and children
  if (state.step === 'success' && state.timeLeft) {
    return (
      <div>
        {/* Countdown Timer */}
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#2c3e50',
          color: 'white',
          padding: '10px 15px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: 'bold',
          zIndex: 1000,
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
        }}>
          ⏰ Temporary Access: {state.timeLeft}
        </div>
        {children}
      </div>
    );
  }

  // Default: render children (normal app if no token or after success without timer)
  return children;
};

const OTPInputScreen = ({ linkData, otpError, isLoading, onOTPSubmit }) => {
  const [otp, setOtp] = useState('');

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        textAlign: 'center',
        padding: '40px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        maxWidth: '500px'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>📱</div>
        <h2 style={{ color: '#2c3e50', margin: '0 0 15px 0' }}>Enter OTP</h2>
        <p style={{ color: '#7f8c8d', margin: '0 0 25px 0', lineHeight: '1.5' }}>
          Please contact the admin to get the OTP for temporary access.
          <br /><br />
          <strong>Email:</strong> {linkData?.tempLinkEmail}
          <br />
          <strong>Expires:</strong> {linkData?.expiresAt ? new Date(linkData.expiresAt).toLocaleString() : 'Unknown'}
        </p>

        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '8px',
          padding: '15px',
          margin: '20px 0',
          textAlign: 'left'
        }}>
          <h4 style={{ color: '#856404', margin: '0 0 10px 0' }}>⚠️ Important:</h4>
          <ul style={{ color: '#856404', margin: '0', paddingLeft: '20px' }}>
            <li>Contact the admin to get the 6-digit OTP</li>
            <li>The OTP is valid for 10 minutes only</li>
            <li>Enter the OTP exactly as provided</li>
          </ul>
        </div>

        <form onSubmit={(e) => onOTPSubmit(e, otp)} style={{ marginTop: '25px' }}>
          <input
            type="text"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="Enter 6-digit OTP"
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #ddd',
              borderRadius: '6px',
              fontSize: '18px',
              textAlign: 'center',
              letterSpacing: '2px',
              marginBottom: '15px',
              boxSizing: 'border-box'
            }}
            maxLength="6"
          />
          {otpError && (
            <p style={{ color: '#e74c3c', margin: '0 0 15px 0', fontSize: '14px' }}>
              {otpError}
            </p>
          )}
          <button
            type="submit"
            disabled={isLoading || otp.length !== 6}
            style={{
              width: '100%',
              backgroundColor: '#27ae60',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '16px',
              cursor: (isLoading || otp.length !== 6) ? 'not-allowed' : 'pointer',
              opacity: (isLoading || otp.length !== 6) ? 0.7 : 1,
              transition: 'background-color 0.3s'
            }}
            onMouseOver={(e) => !isLoading && otp.length === 6 && (e.target.style.backgroundColor = '#229954')}
            onMouseOut={(e) => !isLoading && otp.length === 6 && (e.target.style.backgroundColor = '#27ae60')}
          >
            {isLoading ? 'Verifying...' : 'Verify OTP'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TempAccessHandler;